import {
    ChangeDetectorRef,
    Component,
    OnInit,
} from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Router } from "@angular/router";
import * as moment from "moment";
import { EMPTY, forkJoin, of } from "rxjs";
import { catchError, finalize, switchMap } from "rxjs/operators";
import { Emisor } from "src/app/models/Emisor/Emisor";
import { CertificacionPrecio } from "src/app/models/PlanesUsuario/CertificacionPrecio";
import { EmpresaFacturacion } from "src/app/models/PlanesUsuario/DatosFacturacion";
import { Plan } from "src/app/models/PlanesUsuario/Plan";
import { PlanUsuario } from "src/app/models/PlanesUsuario/PlanUsuario";
import { Resumen } from "src/app/models/PlanesUsuario/Resumen";
import { Usuario } from "src/app/models/Usuario/Usuario";
import { DeudaAdicionalPendiente } from "src/app/models/PlanesUsuario/DeudaAdicionalPendiente";
import { DeudaGraciaPendiente } from "src/app/models/PlanesUsuario/DeudaGraciaPendiente";
import { AuthService } from "src/app/modules/auth";
import { DatosFacturacionService } from "src/app/services/datos-facturacion-service";
import { LocalStorageService } from "src/app/services/local-storage.service";
import { PagoUsuarioService } from "src/app/services/pago-usuario.service";
import { PlanUsuarioService } from "src/app/services/planUsuario.service";
import { TokenStorageService } from "src/app/services/token-storage.service";
import { UsuarioService } from "src/app/services/usuario.service";
import { UtilitiesService } from "src/app/services/utilities.service";
import { WebpayPaymentService } from "src/app/services/webpay-payment.service";
import { SessionUser } from "src/app/shared/models/sessionUser";
import Swal from "sweetalert2";
import { SaldoPagoUi } from "../saldo-favor-bloque/saldo-pago-ui";
import { solicitarRefrescoSaldo } from "src/app/services/saldo.service";
import { FechaCertificacionModalComponent } from "../fecha-certificacion-modal/fecha-certificacion-modal.component";

export type ModoRegularizacion = "regularizar" | "cambiar";

@Component({
    selector: "app-regularizacion",
    templateUrl: "./regularizacion.component.html",
    styleUrls: ["./regularizacion.component.scss"],
})
export class RegularizacionComponent implements OnInit {
    public loading: boolean = false;
    public solicitandoFechaCertificacion = false;

    public user: SessionUser;
    public emisoresSource: Emisor[] = [];
    public emisoresInactivos: Emisor[] = [];
    public currentEmisorObject: Emisor;
    public planes: Plan[] = [];
    public planActual: PlanUsuario;
    public planSeleccionado: Plan;
    public planEnMora: Plan;
    public certificaciones: CertificacionPrecio[] = [];
    public nuevaFechaCaducidad: moment.Moment;
    public fechaActual = moment();
    public resumenCuenta: Resumen;
    public datosFacturacion: EmpresaFacturacion;
    public rowsDatosFacturacion: EmpresaFacturacion[] = [];
    public modo: ModoRegularizacion = "regularizar";
    public deudaAdicionalPendiente: DeudaAdicionalPendiente = new DeudaAdicionalPendiente();
    public pagandoDeudaAdicional = false;
    public deudaGraciaPendiente: DeudaGraciaPendiente = new DeudaGraciaPendiente();
    public pagandoDeudaGracia = false;
    public usuarioBasePuedeAdministrarPlan = false;

    get tieneDeudaAdicionalPendiente(): boolean {
        return this.usuarioBasePuedeAdministrarPlan && (this.deudaAdicionalPendiente?.tieneDeuda ?? false);
    }

    get tieneDeudaGraciaPendiente(): boolean {
        return this.usuarioBasePuedeAdministrarPlan
            && (this.deudaGraciaPendiente?.tieneDeuda ?? false)
            && !this.deudaGraciaPendiente?.ventanaEnCurso;
    }

    get planesNuevos(): Plan[] {
        return this.planes.filter(p => p.antiguedad === 'Nuevo');
    }

    get esMismoPlanEnCambio(): boolean {
        return (
            this.modo === "cambiar" &&
            !!this.planSeleccionado &&
            !!this.planActual &&
            this.planSeleccionado.id === this.planActual.idPlanSf
        );
    }

    constructor(
        private tokenService: TokenStorageService,
        private localStorage: LocalStorageService,
        private planUsuarioService: PlanUsuarioService,
        private userService: UsuarioService,
        private datosFacturacionService: DatosFacturacionService,
        private pagoService: PagoUsuarioService,
        private authService: AuthService,
        public utilities: UtilitiesService,
        private webpayService: WebpayPaymentService,
        private ref: ChangeDetectorRef,
        private router: Router,
        private modalService: NgbModal
    ) {
        this.user = this.tokenService.getUser() as SessionUser;
    }

    ngOnInit(): void {
        this.emisoresSource = JSON.parse(this.localStorage.getEmisores() || "[]") as Emisor[];
        this.emisoresInactivos = this.emisoresSource.filter((e) => !e.activo);

        const emisorActualId = this.tokenService.getCompany();
        this.currentEmisorObject = this.emisoresSource.find(
            (e) => e.emisorId === emisorActualId
        );

        if (this.currentEmisorObject) {
            this.cargarDatos();
        }
    }

    onEmisorSeleccionado(emisor: Emisor): void {
        if (!emisor) return;
        this.currentEmisorObject = emisor;
        this.cargarDatos();
    }

    cargarDatos(): void {
        this.loading = true;
        this.ref.detectChanges();

        const emisorId = this.currentEmisorObject.emisorId;

        this.planUsuarioService.getInitialData().pipe(
            switchMap((data) => {
                this.user = this.tokenService.getUser() as SessionUser;
                this.planes = data.planes;
                this.certificaciones = data.certificaciones;
                return forkJoin({
                    userData: this.userService.getUser(this.user.id, emisorId),
                    facturacionData: this.datosFacturacionService.getData(this.user.id, emisorId),
                    deudaData: this.planUsuarioService.getPendingAdditionalDebt(emisorId),
                });
            }),
            catchError((error) => {
                console.error("Error cargando datos de regularización:", error);
                Swal.fire({
                    title: "Error al cargar datos",
                    text: "No fue posible obtener los datos necesarios. Por favor intente nuevamente.",
                    icon: "error",
                });
                return EMPTY;
            }),
            finalize(() => {
                this.loading = false;
                this.ref.detectChanges();
            })
        ).subscribe(({ userData, facturacionData, deudaData }) => {
            this.usuarioBasePuedeAdministrarPlan = this.hasPlanesUsuario(userData);
            this.handleUserData(userData.data);
            this.rowsDatosFacturacion = facturacionData;
            this.datosFacturacion = facturacionData.some((x) => x.emisorId === this.planActual?.emisorId)
                ? facturacionData.find((x) => x.emisorId === this.planActual?.emisorId)!
                : facturacionData[0];
            this.deudaAdicionalPendiente = this.usuarioBasePuedeAdministrarPlan
                ? (deudaData ?? new DeudaAdicionalPendiente())
                : new DeudaAdicionalPendiente();

            this.cargarDeudaGracia(emisorId);
        });
    }

    private cargarDeudaGracia(emisorId: string): void {
        const idPlanUsuario = this.planActual?.idPlanUsuario;
        if (!this.usuarioBasePuedeAdministrarPlan || !idPlanUsuario) {
            this.deudaGraciaPendiente = new DeudaGraciaPendiente();
            this.solicitarCalculoPlan();
            return;
        }
        this.planUsuarioService.getDeudaGracia(emisorId, idPlanUsuario).pipe(
            catchError(() => of(new DeudaGraciaPendiente()))
        ).subscribe((deudaGraciaData) => {
            this.deudaGraciaPendiente = deudaGraciaData ?? new DeudaGraciaPendiente();
            this.ref.detectChanges();
            this.solicitarCalculoPlan();
        });
    }

    private solicitarCalculoPlan(): void {
        if (this.tieneDeudaGraciaPendiente) return;

        // cargarDatos oculta y destruye temporalmente el resumen mientras obtiene la deuda.
        // Se difiere el evento hasta que el bloque vuelva a estar disponible para recibirlo.
        setTimeout(() => this.pagoService.triggerCalculations(), 0);
    }

    private hasPlanesUsuario(response: any): boolean {
        return Array.isArray(response?.data?.planesUsuario) && response.data.planesUsuario.length > 0;
    }

    private obtenerPlanMasReciente(planesUsuario: PlanUsuario[]): PlanUsuario {
        const planesActivos = planesUsuario.filter(p => p.activo);
        return (planesActivos.length > 0 ? planesActivos : planesUsuario).reduce((masReciente, actual) =>
            new Date(actual.fechaContratacion) > new Date(masReciente.fechaContratacion) ? actual : masReciente
        );
    }

    private handleUserData(userData: Usuario): void {
        const planesDelEmisor = userData.planesUsuario.filter(
            (p) => p.emisorId === this.currentEmisorObject.emisorId
        );
        this.planActual = planesDelEmisor.length > 0
            ? this.obtenerPlanMasReciente(planesDelEmisor)
            : userData.planesUsuario[0];

        this.planEnMora = this.planes.find((x) => x.id === this.planActual?.idPlanSf);
        this.planSeleccionado = this.planEnMora;

        if (this.planActual && !this.planActual.activo) {
            this.certificaciones.forEach((x) => {
                x.idPlanUsuario = this.planActual.idPlanUsuario;
                x.solicitada = userData.certificacionesPendientes.some(
                    (y) => y.codigoPrincipal === x.codigoPrincipal
                );
            });
        }
    }

    setModo(modo: ModoRegularizacion): void {
        this.modo = modo;
        if (modo === 'regularizar') {
            this.planSeleccionado = this.planEnMora;
        }
        this.resumenCuenta = null;
        setTimeout(() => this.pagoService.triggerCalculations(), 0);
    }
    updatePlan(plan: Plan): void {
        this.planSeleccionado = plan;
        setTimeout(() => this.pagoService.triggerCalculations(), 0);
    }

    notifyResumen(resumen: Resumen): void {
        this.nuevaFechaCaducidad = moment(resumen.fechaTermino);
        this.resumenCuenta = resumen;
    }

    pagar(): void {
        if (this.loading || this.solicitandoFechaCertificacion) return;

        if (this.tieneDeudaGraciaPendiente) {
            Swal.fire({
                icon: "warning",
                title: "Tienes una regularización pendiente",
                text: "Para completar la renovación primero debes pagar la regularización por el uso durante el periodo de gracia.",
            });
            return;
        }

        if (this.tieneDeudaAdicionalPendiente) {
            Swal.fire({
                icon: "warning",
                title: "Tienes facturas pendientes por adicionales",
                text: "Para completar la renovación primero debes regularizar la deuda pendiente por adicionales o certificaciones.",
            });
            return;
        }

        if (!this.resumenCuenta || !this.datosFacturacion || !this.planActual) {
            Swal.fire({
                icon: "warning",
                title: "Faltan datos",
                text: "Seleccione un plan y verifique los datos antes de pagar.",
            });
            return;
        }

        if (this.esMismoPlanEnCambio) {
            Swal.fire({
                icon: "warning",
                title: "Selecciona otro plan",
                text: "Para cambiar de plan debes elegir uno diferente al plan actual.",
            });
            return;
        }

        if (this.certificaciones.some(c => c.solicitada)) {
            this.abrirModalFechaCertificacion();
            return;
        }

        this.realizarPago();
    }

    private async abrirModalFechaCertificacion(): Promise<void> {
        this.solicitandoFechaCertificacion = true;
        const modal = this.modalService.open(FechaCertificacionModalComponent, {
            size: "md",
            backdrop: "static",
            keyboard: false,
            ariaLabelledBy: "fecha-certificacion-titulo",
        });

        let fecha: Date;
        try {
            fecha = await modal.result;
        } catch {
            // Cancelar o cerrar la selección de fecha no inicia el pago.
            return;
        } finally {
            this.solicitandoFechaCertificacion = false;
            this.ref.detectChanges();
        }

        this.resumenCuenta.fechaConfirmada = fecha;
        this.realizarPago();
    }

    private realizarPago(): void {
        this.loading = true;
        this.ref.detectChanges();
        this.resumenCuenta.extend = this.modo === "regularizar";
        this.resumenCuenta.change = this.modo === "cambiar";
        this.resumenCuenta.payment = true;

        this.resumenCuenta.idDatosFacturacion = this.datosFacturacion.emisorDatosId;
        this.resumenCuenta.idEmisor = this.planActual.emisorId;
        this.resumenCuenta.idUsuario = this.user.id;
        this.resumenCuenta.idPlanSf =
            this.modo === "cambiar" ? this.planSeleccionado.id : this.planActual.idPlanSf;

        this.certificaciones.forEach((x) => (x.idPlanUsuario = this.planActual.idPlanUsuario));
        this.resumenCuenta.certificaciones = this.certificaciones;

        this.planUsuarioService.doPayment(this.resumenCuenta).subscribe(
            (result) => {
                if (result.status === 200) {
                    if (result.data.pago === "webpay") {
                        this.webpayService.payWithWebpay(result.data.url, result.data.token).subscribe((pagado) => {
                            this.loading = false;
                            this.ref.detectChanges();
                            if (pagado) {
                                Swal.fire({
                                    icon: "success",
                                    title: "¡Pago realizado!",
                                    text: "Tu empresa ha sido reactivada exitosamente.",
                                    confirmButtonText: "Ingresar a SimpleFactura",
                                }).then(() => this.authService.logout().subscribe());
                            }
                        });
                    } else if (SaldoPagoUi.esCoberturaTotal(result.data)) {
						this.loading = false;
						this.ref.detectChanges();
						Swal.fire({
							icon: "success",
							title: "Compra completada con saldo",
							text: "Tu empresa fue reactivada utilizando tu saldo a favor.",
							confirmButtonText: "Ingresar a SimpleFactura",
						}).then(() => this.authService.logout().subscribe());
					} else {
                        this.loading = false;
                        this.ref.detectChanges();
                        Swal.fire({
                            icon: "success",
                            title: "Pago Ingresado",
                            text: "Si el pago fue realizado con éxito se verá reflejado en breves segundos.",
                            confirmButtonText: "Ingresar a SimpleFactura",
                        }).then(() => this.authService.logout().subscribe());
                    }
                }
            },
            (error) => {
                this.loading = false;
                this.ref.detectChanges();
				if (SaldoPagoUi.esConflicto(error)) {
					SaldoPagoUi.limpiarSeleccion(this.resumenCuenta);
					solicitarRefrescoSaldo();
					Swal.fire({
						icon: "warning",
						title: "Tu saldo disponible cambió",
						text: "El saldo disponible cambió. Revisa el total actualizado y confirma nuevamente la compra.",
					});
					return;
				}
                Swal.fire({
                    icon: "error",
                    title: "Error al procesar el pago",
                    text: "No se pudo registrar el pago. " + this.utilities.setErrorMessageFromArray(error.error),
                });
            }
        );
    }

    pagarDeudaAdicional(): void {
        if (!this.tieneDeudaAdicionalPendiente || this.pagandoDeudaAdicional) return;

        if (!this.datosFacturacion?.emisorDatosId) {
            Swal.fire({
                icon: "warning",
                title: "Faltan datos",
                text: "No encontramos los datos de facturación de la empresa. Contacta a soporte para regularizar tu deuda.",
            });
            return;
        }

        this.pagandoDeudaAdicional = true;
        this.ref.detectChanges();

        this.planUsuarioService
            .payPendingAdditionalDebt({
                idUsuario: this.user.id,
                idEmisor: this.planActual?.emisorId ?? this.currentEmisorObject.emisorId,
                idDatosFacturacion: this.datosFacturacion.emisorDatosId,
                idPlanUsuario: this.planActual?.idPlanUsuario,
            })
            .subscribe(
                (result) => {
                    if (result.status === 200 && result.data?.pago === "webpay") {
                        this.webpayService.payWithWebpay(result.data.url, result.data.token).subscribe((pagado) => {
                            this.pagandoDeudaAdicional = false;
                            this.ref.detectChanges();
                            if (pagado) {
                                Swal.fire({
                                    icon: "success",
                                    title: "Deuda regularizada",
                                    text: "El pago de tus facturas pendientes fue registrado. Ahora puedes continuar con la renovación de tu plan.",
                                }).then(() => this.cargarDatos());
                            } else {
                                Swal.fire({
                                    icon: "warning",
                                    title: "Pago en proceso",
                                    text: "No obtuvimos confirmación del pago. Si fue procesado, la deuda dejará de mostrarse en breves minutos.",
                                }).then(() => this.cargarDatos());
                            }
                        });
                    } else {
                        this.pagandoDeudaAdicional = false;
                        this.ref.detectChanges();
                        Swal.fire({
                            icon: "error",
                            title: "Error al procesar el pago",
                            text: "No fue posible iniciar el pago de la deuda. Intenta nuevamente.",
                        });
                    }
                },
                (error) => {
                    this.pagandoDeudaAdicional = false;
                    this.ref.detectChanges();
                    Swal.fire({
                        icon: "error",
                        title: "Error al procesar el pago",
                        text: "No se pudo registrar el pago de la deuda. " + this.utilities.setErrorMessageFromArray(error.error),
                    });
                }
            );
    }

    pagarDeudaGracia(): void {
        if (!this.tieneDeudaGraciaPendiente || this.pagandoDeudaGracia) return;

        if (!this.datosFacturacion?.emisorDatosId) {
            Swal.fire({
                icon: "warning",
                title: "Faltan datos",
                text: "No encontramos los datos de facturación de la empresa. Contacta a soporte para regularizar tu deuda.",
            });
            return;
        }

        this.pagandoDeudaGracia = true;
        this.ref.detectChanges();

        this.planUsuarioService
            .payDeudaGracia({
                idUsuario: this.user.id,
                idEmisor: this.planActual?.emisorId ?? this.currentEmisorObject.emisorId,
                idDatosFacturacion: this.datosFacturacion.emisorDatosId,
                idPlanUsuario: this.planActual?.idPlanUsuario,
            })
            .subscribe(
                (result) => {
                    if (result.status === 200 && result.data?.pago === "webpay") {
                        this.webpayService.payWithWebpay(result.data.url, result.data.token).subscribe((pagado) => {
                            this.pagandoDeudaGracia = false;
                            this.ref.detectChanges();
                            if (pagado) {
                                Swal.fire({
                                    icon: "success",
                                    title: "Regularización pagada",
                                    text: "El pago por el uso durante el periodo de gracia fue registrado. Ahora puedes continuar con la renovación de tu plan.",
                                }).then(() => this.cargarDatos());
                            } else {
                                Swal.fire({
                                    icon: "warning",
                                    title: "Pago en proceso",
                                    text: "No obtuvimos confirmación del pago. Si fue procesado, la deuda dejará de mostrarse en breves minutos.",
                                }).then(() => this.cargarDatos());
                            }
                        });
                    } else {
                        this.pagandoDeudaGracia = false;
                        this.ref.detectChanges();
                        Swal.fire({
                            icon: "error",
                            title: "Error al procesar el pago",
                            text: "No fue posible iniciar el pago de la regularización. Intenta nuevamente.",
                        });
                    }
                },
                (error) => {
                    this.pagandoDeudaGracia = false;
                    this.ref.detectChanges();
                    Swal.fire({
                        icon: "error",
                        title: "Error al procesar el pago",
                        text: "No se pudo registrar el pago de la regularización. " + this.utilities.setErrorMessageFromArray(error.error),
                    });
                }
            );
    }

    salir(): void {
        this.authService.logout().subscribe();
    }
}
